/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import {
  IconChevronDown,
  IconChevronUp,
  IconDeviceFloppy,
  IconEdit,
  IconLoader2,
} from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import Input from "../../components/Input";
import {
  useProfileData,
  useUpdateOutletProfile,
} from "../../apis/profileApis";
import Password from "./_components/Password";

const inputCls = (editable: boolean) =>
  `px-4 py-2 text-sm w-full rounded-full outline-none ${
    editable
      ? "bg-white border border-[#D8D8D8]"
      : "bg-[#F2F2F2] border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
  }`;

const ReadOnly = ({
  title,
  value,
  className = "",
}: {
  title: string;
  value?: string | null;
  className?: string;
}) => (
  <div className={`space-y-1 ${className}`}>
    <label className="text-[#1E293B] text-sm">{title}</label>
    <input
      type="text"
      value={value || "—"}
      disabled
      className={inputCls(false)}
    />
  </div>
);

const Section = ({
  id,
  title,
  openId,
  toggle,
  children,
}: {
  id: string;
  title: string;
  openId: string | null;
  toggle: (id: string) => void;
  children: React.ReactNode;
}) => (
  <div className="border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] space-y-4">
    <div
      onClick={() => toggle(id)}
      className="flex justify-between items-center cursor-pointer"
    >
      <h2 className="uppercase font-medium text-base text-[#212121]">
        {title}
      </h2>
      {openId === id ? (
        <IconChevronUp size={22} stroke={1.5} color="#6B7280" />
      ) : (
        <IconChevronDown size={22} stroke={1.5} color="#6B7280" />
      )}
    </div>
    {openId === id && <div>{children}</div>}
  </div>
);

/** Edit / Save / Cancel buttons */
const EditActions = ({
  isEdit,
  isPending,
  onEdit,
  onSave,
  onCancel,
}: {
  isEdit: boolean;
  isPending?: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}) => (
  <div className="pt-4 flex items-center gap-3">
    <button
      type="button"
      onClick={isEdit ? onSave : onEdit}
      disabled={isPending}
      className="bg-[#FBD037] text-[#212121] font-semibold p-2.5 rounded-full disabled:opacity-50"
    >
      {isPending ? (
        <IconLoader2 size={20} className="animate-spin" />
      ) : isEdit ? (
        <IconDeviceFloppy size={20} />
      ) : (
        <IconEdit size={20} />
      )}
    </button>
    {isEdit && (
      <button
        type="button"
        onClick={onCancel}
        className="px-5 py-2 text-sm rounded-full border border-gray-300 hover:bg-gray-100 transition"
      >
        Cancel
      </button>
    )}
  </div>
);

type OutletFormValues = {
  outletName: string;
  areaCode: string;
  latitude: string;
  longitude: string;
  shortAddress: string;
  address: string;
};

function OutletOwnerProfile() {
  const [isEdit, setIsEdit] = useState(false);
  const [openId, setOpenId] = useState<string | null>("profile");
  const toggle = (id: string) => setOpenId((p) => (p === id ? null : id));

  const { data, refetch } = useProfileData();
  const { mutate, isPending } = useUpdateOutletProfile();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OutletFormValues>();

  useEffect(() => {
    if (!data) return;
    reset({
      outletName: data.outletName || "",
      areaCode: data.areaCode || "",
      latitude: data.latitude || "",
      longitude: data.longitude || "",
      shortAddress: data.shortAddress || "",
      address: data.address || "",
    });
  }, [data, reset]);

  const handleCancel = () => {
    if (data)
      reset({
        outletName: data.outletName,
        areaCode: data.areaCode,
        latitude: data.latitude,
        longitude: data.longitude,
        shortAddress: data.shortAddress,
        address: data.address,
      });
    setIsEdit(false);
  };

  const onSubmit = (formData: OutletFormValues) => {
    mutate(formData, {
      onSuccess: () => {
        refetch();
        setIsEdit(false);
        toast.success("Profile updated successfully.");
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.message || err?.message || "Update failed",
        );
      },
    });
  };

  return (
    <div className="lg:w-10/12 w-11/12 mx-auto text-[#1E293B] space-y-4 py-4">
      <p className="text-2xl text-[#212121] tracking-[-0.5px]">
        Outlet Information
      </p>

      {/* ── Profile Details ── */}
      <Section
        id="profile"
        title="Profile Details"
        openId={openId}
        toggle={toggle}
      >
        <div className="grid grid-cols-6 gap-4">
          {/* ── Editable ── */}
          <Input
            name="outletName"
            type="text"
            title="Outlet Name"
            placeholder="Outlet Name"
            register={register}
            error={errors.outletName}
            validation={{ required: "Outlet name is required" }}
            className="col-span-3"
            inputClassName={inputCls(isEdit)}
            disabled={!isEdit}
          />

          <ReadOnly
            title="Outlet Code"
            value={data?.code}
            className="col-span-3"
          />
          <ReadOnly
            title="Email Address"
            value={data?.email}
            className="col-span-3"
          />
          <ReadOnly
            title="Mobile Number"
            value={data?.mobileNo}
            className="col-span-3"
          />

          <Input
            name="areaCode"
            type="text"
            title="Area Code"
            placeholder=""
            register={register}
            className="col-span-2"
            inputClassName={inputCls(isEdit)}
            disabled={!isEdit}
          />

          <Input
            name="latitude"
            type="text"
            title="Latitude"
            placeholder=""
            register={register}
            className="col-span-2"
            error={errors.latitude}
            inputClassName={inputCls(isEdit)}
            disabled={!isEdit}
          />
          <Input
            name="longitude"
            type="text"
            title="Longitude"
            placeholder=""
            register={register}
            className="col-span-2"
            error={errors.longitude}
            inputClassName={inputCls(isEdit)}
            disabled={!isEdit}
          />
          <ReadOnly
            title="Country"
            value={data?.countryName}
            className="col-span-2"
          />
          <ReadOnly
            title="City"
            className="col-span-2"
            value={data?.cityName}
          />
          <ReadOnly
            title="District"
            className="col-span-2"
            value={data?.districtName}
          />

          <Input
            name="shortAddress"
            type="text"
            title="Short Address"
            placeholder=""
            register={register}
            error={errors.shortAddress}
            className="col-span-3"
            inputClassName={inputCls(isEdit)}
            disabled={!isEdit}
          />
          <Input
            name="address"
            type="text"
            title="Address"
            placeholder=""
            register={register}
            error={errors.address}
            validation={{ required: "Address is required" }}
            className="col-span-3"
            inputClassName={inputCls(isEdit)}
            disabled={!isEdit}
          />
        </div>

        <EditActions
          isEdit={isEdit}
          isPending={isPending}
          onEdit={() => setIsEdit(true)}
          onSave={handleSubmit(onSubmit)}
          onCancel={handleCancel}
        />
      </Section>

      <Section
        id="password"
        title="Reset Password"
        openId={openId}
        toggle={toggle}
      >
        <Password />
      </Section>
    </div>
  );
}

type StaffFormValues = {
  staffName: string;
  phone: string;
};

function OutletStaffProfile() {
  const [isEdit, setIsEdit] = useState(false);
  const [openId, setOpenId] = useState<string | null>("profile");
  const toggle = (id: string) => setOpenId((p) => (p === id ? null : id));

  const { data, refetch } = useProfileData();
  const { mutate, isPending } = useUpdateOutletProfile();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<StaffFormValues>();

  useEffect(() => {
    if (!data) return;
    reset({ staffName: data.staffName || "", phone: data.phone || "" });
  }, [data, reset]);

  const handleCancel = () => {
    if (data)
      reset({ staffName: data.staffName || "", phone: data.phone || "" });
    setIsEdit(false);
  };

  const onSubmit = (formData: StaffFormValues) => {
    if (!isDirty) {
      toast.success("No changes to save.");
      setIsEdit(false);
      return;
    }
    mutate(
      {
        id: data?.id,
        staffName: formData.staffName.trim(),
        phone: formData.phone.trim(),
        type: "outlet",
      },
      {
        onSuccess: () => {
          refetch();
          setIsEdit(false);
          toast.success("Profile updated successfully.");
        },
        onError: (err: any) => {
          toast.error(
            err?.response?.data?.message || err?.message || "Update failed",
          );
        },
      },
    );
  };

  return (
    <div className="lg:w-10/12 w-11/12 mx-auto text-[#1E293B] space-y-4 py-4">
      {/* Header with photo */}
      <div className="flex items-center gap-4">
        <div>
          <p className="text-2xl text-[#212121] tracking-[-0.5px]">
            My Profile
          </p>
        </div>
      </div>

      <Section
        id="profile"
        title="Personal Details"
        openId={openId}
        toggle={toggle}
      >
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3 space-y-1">
            <label className="text-[#1E293B] text-sm">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register("staffName", {
                required: "Full name is required",
                pattern: {
                  value: /^(?=.*[A-Za-z])[A-Za-z0-9\s-]+$/,
                  message: "Name must include at least one letter",
                },
              })}
              disabled={!isEdit}
              placeholder="Enter Full Name"
              className={inputCls(isEdit)}
            />
            {errors.staffName && (
              <span className="text-red-500 text-xs block">
                {errors.staffName.message}
              </span>
            )}
          </div>

          <div className="col-span-3 space-y-1">
            <label className="text-[#1E293B] text-sm">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              {...register("phone", {
                required: "Phone number is required",
                validate: (v) =>
                  v.trim().length >= 8 || "Enter a valid phone number",
              })}
              disabled={!isEdit}
              placeholder="Enter Phone Number"
              className={inputCls(isEdit)}
            />
            {errors.phone && (
              <span className="text-red-500 text-xs block">
                {errors.phone.message}
              </span>
            )}
          </div>

          <ReadOnly title="Email" value={data?.email} className="col-span-3" />
          <ReadOnly
            title="Start Date"
            value={data?.startDate}
            className="col-span-3"
          />

          <div className="col-span-3 space-y-1">
            <label className="text-[#1E293B] text-sm">Iqama Number</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={data?.iqamaNo || "—"}
                disabled
                className={`${inputCls(false)} flex-1`}
              />
              {data?.iqamaDoc ? (
                <a
                  href={`${process.env.REACT_APP_IMAGE_BASE_URL}/${data.iqamaDoc}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View Iqama Document"
                  className="shrink-0 flex items-center justify-center h-[38px] w-[44px] border border-[#D2D2D2] rounded-xl bg-[#FBD037]/20 hover:bg-[#FBD037]/50 transition"
                >
                  <img
                    src="/img/download-file-icon.svg"
                    alt="view"
                    className="h-5 w-5"
                  />
                </a>
              ) : (
                <span className="shrink-0 text-sm text-gray-400 px-2"></span>
              )}
            </div>
          </div>
          <ReadOnly
            title="Assigned Outlet"
            value={data?.outletName}
            className="col-span-3"
          />
          <ReadOnly
            title="Country"
            value={data?.countryName}
            className="col-span-2"
          />
          <ReadOnly
            title="City"
            value={data?.cityName}
            className="col-span-2"
          />

          <ReadOnly
            title="District"
            value={data?.districtName}
            className="col-span-2"
          />
          <ReadOnly
            title="Short Address"
            value={data.shortAddress}
            className="col-span-3"
          />
          <ReadOnly
            title="Address"
            value={data?.address}
            className="col-span-3"
          />
        </div>

        <EditActions
          isEdit={isEdit}
          isPending={isPending}
          onEdit={() => setIsEdit(true)}
          onSave={handleSubmit(onSubmit)}
          onCancel={handleCancel}
        />
      </Section>

      <Section
        id="password"
        title="Reset Password"
        openId={openId}
        toggle={toggle}
      >
        <Password />
      </Section>
    </div>
  );
}

export default function Profile() {
  const profile = useSelector((state: any) => state.auth.profile);

  if (profile?.userType === "STAFF") {
    return <OutletStaffProfile />;
  }

  return <OutletOwnerProfile />;
}
